/**
 * Sawyer B1 — the Bed field on a booking being made (Quick Book) or edited
 * (Edit reservation).
 *
 * Sawyer: "when I'm booking, he's trying to select a bed over nights … I
 * tried this and I couldn't get it to work." The calendar's own `BedPicker`
 * (LodgingCalendar.tsx) decides from the same verdict; this is a separate
 * component so that file stays with the lanes that edit it.
 *
 * DESIGN-PHILOSOPHY:
 *  - #2 the next action is obvious — every state says what to do next, and a
 *    bed is never required (the caller never disables its primary for it).
 *  - #5 never lie — the list is the lodging read judged by `bedPickerState`;
 *    zero beds reads "No beds are set up yet", never "none free".
 *  - #7 built for hands — a native select, 44px, full width.
 *  - Five states: default (offer) · loading ("Checking beds…") · empty
 *    (no beds / none free) · error (with Try again) · stale (re-read on
 *    focus after a minute; the server's refusal is final — see useLodgingRead).
 *
 * `onChange` hands back the whole unit, not just its id, so a neighbour lane
 * (lockers, lane 06) can default a field from the bed without changing this
 * signature. The bed order is the server's (no client re-sort): if a natural
 * order lands in the lodging read or a shared sort helper, use it at the
 * option map below.
 */
import type { LodgingUnit } from '../../lib/api.js';
import { bedChoiceLabel, bedFieldState, noBedsCopy } from '../../lib/booking-bed.js';
import type { Placement } from '../../lib/lodging-moves.js';
import type { LodgingRead } from './useLodgingRead.js';
import './BookingExtras.css';

export interface BookingBedFieldProps {
  read: LodgingRead;
  /** A customer and at least one dog are chosen. */
  hasParty: boolean;
  /** At least one night (pick-up after drop-off). */
  overnight: boolean;
  placement: Placement | null;
  /** '' = no bed. */
  value: string;
  onChange: (unit: LodgingUnit | null) => void;
  /** Edit only — the stay's current bed, listed first as "(current)". */
  current?: { unitId: string; unitName: string } | null;
  /** Settings → Lodging is editable by this session (refuter C-11). */
  canEditBeds: boolean;
  /** Copy differs slightly: "book" on Quick Book, "save" on Edit reservation. */
  context: 'create' | 'edit';
  disabled?: boolean;
}

export function BookingBedField({
  read,
  hasParty,
  overnight,
  placement,
  value,
  onChange,
  current = null,
  canEditBeds,
  context,
  disabled = false,
}: BookingBedFieldProps): JSX.Element {
  const state = bedFieldState({
    hasParty,
    overnight,
    loading: read.loading,
    error: read.error,
    data: read.data,
    placement,
  });

  const stateLine = (kind: string, text: string, role?: 'status' | 'alert') => (
    <p
      className="ferro-quickbook__hint ferro-booking-extras__state"
      role={role}
      data-testid={`booking-bed-state-${kind}`}
    >
      {text}
    </p>
  );

  let body: JSX.Element;
  if (state.kind === 'need-draft') {
    body = stateLine('need-draft', 'Pick the customer and dogs first — the bed list depends on who is staying.');
  } else if (state.kind === 'same-day') {
    body = stateLine(
      'same-day',
      context === 'create'
        ? 'A bed is for overnight stays — pick a pick-up date after drop-off.'
        : 'A bed is for overnight stays — pick a last day after the first day.',
    );
  } else if (state.kind === 'loading') {
    body = stateLine('loading', 'Checking beds…', 'status');
  } else if (state.kind === 'error') {
    body = (
      <div className="ferro-quickbook__hint ferro-booking-extras__state" role="alert" data-testid="booking-bed-state-error">
        <span>
          {context === 'create'
            ? "Couldn't load the beds. You can still book without one."
            : "Couldn't load the beds. You can still save the other changes."}
        </span>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost ferro-booking-extras__retry"
          onClick={read.retry}
          data-testid="booking-bed-retry"
        >
          Try again
        </button>
      </div>
    );
  } else if (state.kind === 'no-beds') {
    body = stateLine('no-beds', noBedsCopy(canEditBeds));
  } else if (state.kind === 'none-free' && !current) {
    body = stateLine(
      'none-free',
      context === 'create'
        ? 'No bed is free for all of these nights — book without one and place the dog from the Lodging calendar.'
        : 'No bed is free for all of these nights — save without one and place the dog from the Lodging calendar.',
    );
  } else {
    const offered = state.kind === 'offer' ? state.beds : [];
    const others = current ? offered.filter((u) => u.id !== current.unitId) : offered;
    const currentStillFree = current ? offered.some((u) => u.id === current.unitId) : false;
    body = (
      <>
        <select
          className="ferro-field__input ferro-booking-extras__select"
          value={value}
          disabled={disabled || read.loading}
          onFocus={read.refreshIfStale}
          onChange={(e) => {
            const id = e.target.value;
            if (id === '') return onChange(null);
            const unit =
              offered.find((u) => u.id === id) ??
              (current && current.unitId === id ? read.data?.units.find((u) => u.id === id) ?? null : null);
            onChange(unit ?? null);
          }}
          aria-label="Bed (optional)"
          data-testid="booking-bed-select"
        >
          <option value="">{context === 'create' ? 'No bed yet' : 'No bed'}</option>
          {current ? (
            <option value={current.unitId}>
              {currentStillFree ? `${current.unitName} (current)` : `${current.unitName} (current — not free on these nights)`}
            </option>
          ) : null}
          {/* Lane 06 (beds-lockers-runcard) switches this text to its bedOptionLabel (size + natural order). */}
          {others.map((u) => (
            <option key={u.id} value={u.id}>
              {bedChoiceLabel(u)}
            </option>
          ))}
        </select>
        {read.loading ? stateLine('refreshing', 'Checking beds…', 'status') : null}
        {state.kind === 'none-free'
          ? stateLine(
              'none-free',
              'No other bed is free for all of these nights — save without one and place the dog from the Lodging calendar.',
            )
          : null}
      </>
    );
  }

  return (
    <div className="ferro-field" data-testid="booking-bed-field">
      <span className="ferro-field__label">Bed (optional)</span>
      {body}
    </div>
  );
}
